import type { Comment } from '@/@types/comment'
import createShareImageMenuItem from '@/components/ShareImage/createShareImageMenuItem'
import BubbleIcon from '@/assets/bubble.svg?react'
import LikeIcon from '@/assets/like.svg?react'
import useUser from '@/utils/hooks/useUser'
import clsx from 'clsx'
import type { HTMLAttributes } from 'react'
import toast from 'react-hot-toast'
import ActionButton from '../ActionButton'
import { BlurTextMode, FeaturableBlurTextSwitch } from '../BlurTextSwitch'
import type { MenuItemData } from '../DropdownMenu/types'
import { showConfirm } from '../Modal/utils'
import { ACTION_MENU_LOCALE, ActionMenu } from '../Post/config'
import styles from './CommentActions.module.css'
import { useCommentActionsContext } from './CommentActionsContext'
import CommentActionBar from './CommentActionBar'

export interface CommentActionsProps extends HTMLAttributes<HTMLDivElement> {
	data: Comment
}

export default function CommentActions({ data, className, ...props }: CommentActionsProps) {
	const {
		refresh: refreshList,
		onComment,
		onDelete,
		onToggleLike,
		onTogglePin,
	} = useCommentActionsContext()
	const user = useUser(void 0)

	const menu: MenuItemData[] = [ACTION_MENU_LOCALE[ActionMenu.COPY_LINK]]
	const shareImageItem = createShareImageMenuItem({ kind: 'comment', data })
	if (shareImageItem) menu.push(shareImageItem)
	const isMyContent = Boolean(user && data.user?.id === user.id)
	if (isMyContent) {
		menu.push(ACTION_MENU_LOCALE[data.isPinned ? ActionMenu.UNPIN : ActionMenu.PIN])
		menu.push(ACTION_MENU_LOCALE[ActionMenu.DELETE])
	}

	const handleMoreActionMenuItemClick = async (menuId: string) => {
		try {
			switch (menuId as ActionMenu) {
				case ActionMenu.COPY_LINK: {
					const commentUsername = data.user?.username
					if (!commentUsername) return

					const url = new URL(
						`/u/${commentUsername}/post/${data.targetId}/${data.id}`,
						window.location.origin
					)
					await navigator.clipboard.writeText(url.toString())
					toast.success('复制成功')
					break
				}
				case ActionMenu.PIN:
				case ActionMenu.UNPIN:
					await onTogglePin(data)
					await refreshList?.()
					break
				case ActionMenu.DELETE:
					await showConfirm(
						'确定删除吗？',
						<div className={styles.confirmText}>删除后无法恢复</div>
					)
					await onDelete(data)
					break
			}
		} catch {
			if (menuId === ActionMenu.COPY_LINK) toast.error('复制失败')
		}
	}

	return (
		<CommentActionBar
			className={className}
			menu={menu}
			onMenuSelect={handleMoreActionMenuItemClick}
			{...props}
		>
			<ActionButton
				tooltip="点赞"
				onClick={() => void onToggleLike(data)}
				icon={
					<LikeIcon
						width="20px"
						height="20px"
						className={clsx(styles.likeIcon, data?.liked && styles.likeIconLiked)}
					/>
				}
			>
				<FeaturableBlurTextSwitch
					data={(data?.likeCount ?? 0).toString()}
					mode={BlurTextMode.SMALL}
				/>
			</ActionButton>

			<ActionButton
				tooltip="回复"
				onClick={() => {
					onComment({
						to: data,
						threadId: data?.threadId,
					})
				}}
				icon={<BubbleIcon width="20px" height="20px" />}
			>
				<FeaturableBlurTextSwitch
					data={(data?.replyCount ?? 0).toString()}
					mode={BlurTextMode.SMALL}
				/>
			</ActionButton>
		</CommentActionBar>
	)
}
